import React, { useMemo, useRef, useState } from "react";
import Modal from "@/components/chat/Modal";
import { Image as ImageIcon, Video, X, Loader2, Plus, Tag, Camera, Lightbulb } from "lucide-react";
import api from "@/lib/api";
import { uploadFileChunked, fileUrl } from "@/lib/uploads";
import { compressImage, dataSaverOn } from "@/lib/imageCompress";
import { encodeImageMeta } from "@/lib/blur";
import HelpGuide from "@/components/shared/HelpGuide";
import { useI18n } from "@/context/I18nContext";
import { symbolFor } from "@/lib/currency";
import { toast } from "sonner";

const cartesian = (arrays) => arrays.reduce((acc, arr) => acc.flatMap((a) => arr.map((b) => [...a, b])), [[]]);

const CLS = "w-full bg-[var(--rc-bg)] border border-[var(--rc-border)] rounded-xl px-3 py-2.5 focus:outline-none focus:border-[var(--rc-gold)]/50";

// Hoisted to module scope so inputs keep focus while typing (a component defined
// inside render is a new type each keystroke → React remounts it → keyboard closes).
const Field = ({ label, children }) => (
  <div><label className="block text-xs text-[var(--rc-muted)] mb-1">{label}</label>{children}</div>
);

export default function ProductComposer({ product, categories = [], currency = "USD", onClose, onSaved }) {
  const { t } = useI18n();
  const editing = !!product;
  const [title, setTitle] = useState(product?.title || "");
  const [price, setPrice] = useState(product?.price ?? product?.base_price ?? "");
  const [description, setDescription] = useState(product?.description || "");
  const [category, setCategory] = useState(product?.category || "");
  const [location, setLocation] = useState(product?.location || "");
  const [images, setImages] = useState(product?.images || []);
  const [video, setVideo] = useState(product?.video || null);
  const [stock, setStock] = useState(product?.stock ?? "");
  const [options, setOptions] = useState(
    (product?.variant_options || []).map((o) => ({ name: o.name, values: (o.values || []).join(", ") }))
  );
  const [variantStock, setVariantStock] = useState(() => {
    const m = {}; (product?.variants || []).forEach((v) => { m[v.key] = v.stock; }); return m;
  });
  const [promoPrice, setPromoPrice] = useState(product?.promo_price ?? "");
  const [promoDays, setPromoDays] = useState(() => {
    if (!product?.promo_until) return "";
    const d = Math.ceil((new Date(product.promo_until) - Date.now()) / 86400000);
    return d > 0 ? d : "";
  });
  const [busy, setBusy] = useState(false);
  const [photoTips, setPhotoTips] = useState(false);
  const imgRef = useRef(null);
  const camRef = useRef(null);
  const vidRef = useRef(null);
  const cls = CLS;

  const combos = useMemo(() => {
    const groups = options.map((o) => o.values.split(",").map((s) => s.trim()).filter(Boolean)).filter((g) => g.length);
    if (!groups.length) return [];
    return cartesian(groups).map((c) => c.join(" / "));
  }, [options]);

  const pickImages = async (e) => {
    const files = Array.from(e.target.files || []); e.target.value = "";
    const room = 5 - images.length;
    for (const f of files.slice(0, Math.max(0, room))) {
      try {
        setBusy(true);
        const toSend = dataSaverOn() ? await compressImage(f) : f;
        const meta = await encodeImageMeta(toSend).catch(() => ({}));
        const { url } = await uploadFileChunked(toSend, null, "marketplace");
        setImages((m) => [...m, { url, type: "image", blur_hash: meta.blur_hash, w: meta.media_w, h: meta.media_h }].slice(0, 5));
      } catch { toast.error(t("m_upload_failed")); } finally { setBusy(false); }
    }
  };
  const pickVideo = async (e) => {
    const f = e.target.files?.[0]; e.target.value = ""; if (!f) return;
    try { setBusy(true); const { url } = await uploadFileChunked(f, null, "marketplace"); setVideo({ url, type: "video" }); }
    catch { toast.error(t("m_upload_failed")); } finally { setBusy(false); }
  };

  const addOption = () => { if (options.length < 3) setOptions([...options, { name: "", values: "" }]); };
  const setOpt = (i, patch) => setOptions(options.map((o, idx) => idx === i ? { ...o, ...patch } : o));
  const rmOption = (i) => setOptions(options.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!title.trim()) return toast.error(t("m_add_title"));
    try {
      setBusy(true);
      const variant_options = options
        .map((o) => ({ name: o.name.trim(), values: o.values.split(",").map((s) => s.trim()).filter(Boolean) }))
        .filter((o) => o.name && o.values.length);
      const variants = combos.map((key) => ({ key, stock: parseInt(variantStock[key], 10) || 0 }));
      let promo_until = null;
      const pp = parseFloat(promoPrice);
      const pd = parseInt(promoDays, 10);
      if (pp > 0 && pd > 0) promo_until = new Date(Date.now() + pd * 86400000).toISOString();
      const payload = {
        title: title.trim(), price: parseFloat(price) || 0, description, category, location,
        images: images.slice(0, 5).map(({ url, type, blur_hash, w, h }) => ({ url, type, blur_hash, w, h })),
        video: video ? { url: video.url, type: "video" } : null,
        stock: variants.length ? null : (stock === "" ? null : parseInt(stock, 10) || 0),
        variant_options, variants,
        promo_price: pp > 0 ? pp : null, promo_until,
      };
      const { data } = editing ? await api.put(`/market/products/${product.id}`, payload) : await api.post("/market/products", payload);
      onSaved && onSaved(data); toast.success(editing ? t("m_updated") : t("m_listed")); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || t("m_could_not_save")); } finally { setBusy(false); }
  };

  return (
    <Modal title={editing ? t("m_edit_item") : t("m_list_item")} onClose={onClose}>
      <div className="space-y-3" data-testid="product-composer">
        <Field label={t("m_title")}><input value={title} onChange={(e) => setTitle(e.target.value)} className={cls} data-testid="product-title" placeholder={t("m_ph_title")} /></Field>
        <Field label={t("m_price")}>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[var(--rc-muted)] pointer-events-none" data-testid="product-price-symbol">{symbolFor(currency)}</span>
            <input type="number" value={price} onChange={(e) => setPrice(e.target.value)} className={`${cls} pl-12`} data-testid="product-price" placeholder="0" />
          </div>
        </Field>
        <Field label={t("m_description")}><textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} className={`${cls} resize-none`} data-testid="product-description" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("m_category")}>
            {categories.length ? (
              <select value={category} onChange={(e) => setCategory(e.target.value)} className={cls} data-testid="product-category">
                <option value="">{t("m_select")}</option>
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            ) : <input value={category} onChange={(e) => setCategory(e.target.value)} className={cls} data-testid="product-category" placeholder={t("m_ph_category")} />}
          </Field>
          <Field label={t("m_location")}><input value={location} onChange={(e) => setLocation(e.target.value)} className={cls} data-testid="product-location" placeholder={t("m_ph_location")} /></Field>
        </div>

        {/* Photos */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-xs text-[var(--rc-muted)]">{t("m_photos_max")}</label>
            <button type="button" onClick={() => setPhotoTips(true)} data-testid="photo-tips-btn"
              className="flex items-center gap-1 text-xs text-[var(--rc-gold)] font-medium"><Lightbulb size={13} /> {t("photo_tips")}</button>
          </div>
          <input ref={imgRef} type="file" accept="image/*" multiple hidden onChange={pickImages} data-testid="product-image-input" />
          <input ref={camRef} type="file" accept="image/*" capture="environment" hidden onChange={pickImages} data-testid="product-camera-input" />
          <div className="flex items-center gap-2">
            <button onClick={() => imgRef.current?.click()} disabled={images.length >= 5} data-testid="product-add-image" className="flex items-center gap-2 rounded-xl border border-[var(--rc-border)] px-3 py-2 text-sm text-[var(--rc-gold)] disabled:opacity-40"><ImageIcon size={16} /> {t("m_add_photos")}</button>
            <button onClick={() => camRef.current?.click()} disabled={images.length >= 5} data-testid="product-take-photo" className="flex items-center gap-2 rounded-xl border border-[var(--rc-border)] px-3 py-2 text-sm text-[var(--rc-gold)] disabled:opacity-40"><Camera size={16} /> {t("m_take_photo")}</button>
          </div>
          <div className="text-[11px] text-[var(--rc-muted)] mt-1" data-testid="product-photos-remaining">{images.length >= 5 ? t("m_photos_full") : `${5 - images.length} ${t("m_photos_left")} (${images.length}/5)`}</div>
          {images.length > 0 && <div className="mt-2 grid grid-cols-5 gap-2">{images.map((m, i) => (
            <div key={i} className="relative aspect-square rounded-lg overflow-hidden bg-[var(--rc-bg)]">
              <img src={m.url.startsWith("/api") ? fileUrl(m.url) : m.url} alt="" className="h-full w-full object-cover" />
              <button onClick={() => setImages((im) => im.filter((_, idx) => idx !== i))} className="absolute top-1 right-1 h-5 w-5 grid place-items-center rounded-full bg-black/60 text-white"><X size={11} /></button>
            </div>
          ))}</div>}
        </div>

        {/* Video */}
        <div>
          <label className="block text-xs text-[var(--rc-muted)] mb-1">{t("m_video")}</label>
          <input ref={vidRef} type="file" accept="video/*" hidden onChange={pickVideo} data-testid="product-video-input" />
          {video ? (
            <div className="flex items-center gap-2 rounded-xl border border-[var(--rc-border)] px-3 py-2 text-sm">
              <Video size={16} className="text-[var(--rc-gold)]" /> <span className="flex-1 truncate text-[var(--rc-muted)]">{t("m_video")}</span>
              <button onClick={() => setVideo(null)} data-testid="product-remove-video" className="text-red-400 text-xs">{t("m_remove_video")}</button>
            </div>
          ) : (
            <button onClick={() => vidRef.current?.click()} data-testid="product-add-video" className="flex items-center gap-2 rounded-xl border border-[var(--rc-border)] px-3 py-2 text-sm text-[var(--rc-gold)]"><Video size={16} /> {t("m_add_video")}</button>
          )}
        </div>

        {/* Variants */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs text-[var(--rc-muted)]">{t("m_variants")}</label>
            {options.length < 3 && <button onClick={addOption} data-testid="add-variant-option" className="flex items-center gap-1 text-xs text-[var(--rc-gold)]"><Plus size={13} /> {t("m_add_option")}</button>}
          </div>
          {options.map((o, i) => (
            <div key={i} className="flex items-center gap-2 mb-2" data-testid={`variant-option-${i}`}>
              <input value={o.name} onChange={(e) => setOpt(i, { name: e.target.value })} placeholder={t("m_option_name_ph")} data-testid={`variant-name-${i}`} className={`${cls} flex-1`} />
              <input value={o.values} onChange={(e) => setOpt(i, { values: e.target.value })} placeholder={t("m_option_values_ph")} data-testid={`variant-values-${i}`} className={`${cls} flex-[2]`} />
              <button onClick={() => rmOption(i)} className="text-red-400 shrink-0"><X size={16} /></button>
            </div>
          ))}
          {combos.length > 0 ? (
            <div className="rounded-xl border border-[var(--rc-border)] p-2 space-y-1.5">
              <div className="text-[11px] text-[var(--rc-muted)]">{t("m_variant_stock")}</div>
              {combos.map((key) => (
                <div key={key} className="flex items-center gap-2" data-testid={`variant-combo-${key}`}>
                  <span className="flex-1 text-sm truncate">{key}</span>
                  <input type="number" min={0} value={variantStock[key] ?? ""} onChange={(e) => setVariantStock({ ...variantStock, [key]: e.target.value })}
                    data-testid={`variant-stock-${key}`} placeholder="0" className="w-20 bg-[var(--rc-bg)] border border-[var(--rc-border)] rounded-lg px-2 py-1.5 text-sm" />
                </div>
              ))}
            </div>
          ) : (
            <Field label={t("m_stock_label")}><input type="number" min={0} value={stock} onChange={(e) => setStock(e.target.value)} className={cls} data-testid="product-stock" placeholder={t("m_stock_ph")} /></Field>
          )}
        </div>

        {/* Promotion */}
        <div className="rounded-xl border border-[var(--rc-border)] p-3">
          <div className="flex items-center gap-1.5 text-sm text-[var(--rc-gold)] mb-2"><Tag size={15} /> {t("m_promo")}</div>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("m_promo_price")}><input type="number" min={0} value={promoPrice} onChange={(e) => setPromoPrice(e.target.value)} className={cls} data-testid="product-promo-price" placeholder="0" /></Field>
            <Field label={t("m_promo_days")}><input type="number" min={0} value={promoDays} onChange={(e) => setPromoDays(e.target.value)} className={cls} data-testid="product-promo-days" placeholder="7" /></Field>
          </div>
        </div>

        <button onClick={save} disabled={busy} data-testid="product-save" className="w-full rounded-xl bg-[var(--rc-gold)] text-black font-semibold py-3 flex items-center justify-center gap-2">
          {busy && <Loader2 size={16} className="animate-spin" />} {editing ? t("m_save_changes") : t("m_publish")}
        </button>
      </div>
      {photoTips && (
        <HelpGuide testid="photo-tips-overlay" title={t("photo_tips_title")}
          steps={[t("photo_tip_1"), t("photo_tip_2"), t("photo_tip_3"), t("photo_tip_4"), t("photo_tip_5")]}
          onClose={() => setPhotoTips(false)} />
      )}
    </Modal>
  );
}
